import React, { useState, useEffect, useRef } from 'react'

import { oneOf, bool, array, string } from 'prop-types'

import useApiGet from 'Pods/Shared/Hooks/useApiGet'

import RecentItems from './Components/RecentItems/RecentItems'
import WishlistItems from './Components/WishlistItems/WishlistItems'

function AdditionalContent({
  variation,
  isCartBelowThreshold,
  isCartAboveThreshold,
  shouldShowCartProgress,
  cartItemIds,
  locale = 'en',
}) {
  const [wishlistItems, setWishlistItems] = useState({
    data: [],
    meta: {},
    included: [],
    page: 1,
    isLastPageLoaded: true,
  })

  const [recentItems, setRecentItems] = useState({
    data: [],
    meta: {},
    included: [],
    page: 1,
    isLastPageLoaded: true,
  })

  const { getData: getWishlistData, isLoading: isWishlistLoading, error: isWishListErrored } = useApiGet()

  const { getData: getRecentData, isLoading: isRecentLoading, error: isRecentErrored } = useApiGet()

  const lastCartIds = useRef(cartItemIds)

  useEffect(() => {
    fetchWishlistData()
    fetchRecentData()
  }, [])

  useEffect(() => {
    if (variation === 'wishlist-tab' && cartItemIds.join(',') !== lastCartIds.current.join(',')) {
      fetchWishlistData(1, true)
      lastCartIds.current = cartItemIds
    }
  }, [cartItemIds])

  function fetchWishlistData(pageNr = 1, replace = false) {
    if (isWishlistLoading || (pageNr !== 1 && wishlistItems.isLastPageLoaded)) return

    getWishlistData({
      path: '/api/internal/cg/wishlist-items',
      params: { page: pageNr },
      onSuccess: (response) => {
        const parseNumber = (value, fallback) => {
          const parsed = Number(value)
          return Number.isFinite(parsed) ? parsed : fallback
        }

        const responseData = response.data.data || []
        const responseMeta = response.data.meta || {}
        const filteredData = responseData.filter((item) => !cartItemIds.includes(item.id))
        const currentPage = parseNumber(pageNr, 1)
        const totalPages = parseNumber(responseMeta.totalPages, currentPage)
        const reachedLastPage = responseData.length === 0 || currentPage >= totalPages

        if (replace || pageNr === 1) {
          setWishlistItems({
            data: filteredData,
            meta: responseMeta,
            included: response.data.included,
            page: currentPage,
            isLastPageLoaded: reachedLastPage,
          })
        } else {
          const existingIds = wishlistItems.data.map((item) => item.id)
          const newItems = filteredData.filter((item) => !existingIds.includes(item.id))

          const combinedData = wishlistItems.data.concat(newItems)

          setWishlistItems((prev) => ({
            data: combinedData,
            meta: prev.meta,
            included: (prev.included || []).concat(response.data.included),
            page: currentPage,
            isLastPageLoaded:
              responseData.length === 0
              || currentPage >= parseNumber(prev.meta?.totalPages, totalPages),
          }))
        }
      },
    })
  }

  function fetchRecentData(pageNr = 1) {
    if (isRecentLoading || (pageNr !== 1 && recentItems.isLastPageLoaded)) {
      return false
    }

    getRecentData({
      path: '/api/internal/cg/recent-items',
      params: { page: pageNr },
      onSuccess: (response) => {
        const newState = {
          data: recentItems.data.concat(response.data.data),
          meta: pageNr === 1 ? response.data.meta : recentItems.meta,
          included: (recentItems.included || []).concat(response.data.included),
          page: pageNr,
        }

        setRecentItems({
          ...newState,
          isLastPageLoaded: newState.data.length >= newState.meta.totalCount,
        })
      },
    })
  }

  function prepareTrackData(attributes) {
    let trackableAttributes = {}
    const source = attributes.source || ''

    trackableAttributes = {
      item_id: String(attributes.itemId),
      item_name: attributes.itemTitle,
      affiliation: attributes.affiliation || '',
      currency: 'USD',
      index: attributes.index,
      item_brand: attributes.itemBrand,
      item_category: attributes.itemCategory,
      item_category2: attributes.itemCategory2,
      item_category3: attributes.itemCategory3,
      item_category4: attributes.itemCategory4,
      item_category5: attributes.itemCategory5,
      item_list_id: attributes.itemListId,
      item_list_name: source,
      item_variant: attributes.itemVariant || attributes.productType,
      location_id: Array.isArray(attributes.locationId) ? attributes.locationId.join('_') : attributes.locationId,
      price: parseFloat(attributes.price),
      quantity: 1,
    }

    trackableAttributes.discount = attributes.discount > 0 ? parseFloat(attributes.discount) : 0

    return trackableAttributes
  }

  function getUserId() {
    if (typeof window !== 'undefined') {
      // take user id from APP.config.userId - for slow application
      // or from window.pageConfig.currentUserId - for fast application
      return window.App?.config?.userId || window.pageConfig?.currentUserId
    }

    return ''
  }

  function trackAddToCart(attributes) {
    const item = prepareTrackData(attributes)

    if (window.dataLayer) {
      window.dataLayer.push({
        event: 'add_to_cart',
        user_id: getUserId(),
        event_source: attributes.source,
        ecommerce: {
          currency: 'USD',
          value: item.price,
          items: [item],
        },
        _clear: true,
      })
    }
  }


  const cartProps = {
    isCartBelowThreshold,
    isCartAboveThreshold,
    shouldShowCartProgress,
  }

  if (variation === 'wishlist-tab') {
    return (
      <WishlistItems
        wishlistItems={wishlistItems}
        isWishlistLoading={isWishlistLoading}
        isWishListErrored={!!isWishListErrored}
        fetchData={fetchWishlistData}
        locale={locale}
        trackAddToCart={trackAddToCart}
        {...cartProps}
      />
    )
  }

  return (
    <RecentItems
      recentItems={recentItems}
      isRecentLoading={isRecentLoading}
      isRecentErrored={!!isRecentErrored}
      fetchData={fetchRecentData}
      trackAddToCart={trackAddToCart}
      locale={locale}
      {...cartProps}
    />
  )
}

AdditionalContent.propTypes = {
  variation: oneOf(['wishlist-tab', 'recently-viewed-tab']).isRequired,
  isCartBelowThreshold: bool.isRequired,
  isCartAboveThreshold: bool.isRequired,
  shouldShowCartProgress: bool.isRequired,
  cartItemIds: array.isRequired,
  locale: string,
}

export default AdditionalContent
